import { useNavigation, useRoute } from "@react-navigation/native";
import { SiteSelect } from "components/form/SiteSelect";
import Container from "components/general/Container";
import EnhancedDateSelect from "components/general/EnhancedDateSelect";
import ScrollContainer from "components/general/ScrollContainer";
import TitledContainer from "components/general/TitledContainer";
import { Box, Button, FormattedDate, Typography } from "components/ui";
import React, { useState } from "react";
import { usePostBackordersMutation, usePostDifotMutation, usePostOperationsMutation, usePostPurchasesMutation, usePostRechargeableitemsMutation, usePostStockusagebysiteMutation, usePostStockusagebysitebymonthMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { ReportTitles } from "..";
import { useLocalSearchParams, useRouter } from "expo-router";
import { handleFileResponse } from "utils/fileDownload";

interface DateRange {
	startDate: Date;
	endDate: Date;
}

interface RouteParams {
	reportId: string;
}

export default function Page() {
	const isMobile = useIsMobile();
	const router = useRouter();
	const { id: reportId } = useLocalSearchParams<{ id: string }>();

	// Determine if we need monthOnly mode based on the report type
	const isOperationsReport = Number(reportId) === 1;

	const threeMonthsAgo = new Date();
	threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
	threeMonthsAgo.setHours(0, 0, 0, 0); // Reset to start of day

	const today = new Date();
	today.setHours(23, 59, 59, 999); // Set to end of the day
	const [startDate, setStartDate] = React.useState<Date>(threeMonthsAgo);
	const [endDate, setEndDate] = React.useState<Date>(today);
	const [groupIdCollection, setGroupIdCollection] = React.useState<number[]>([]);

	const [isSubscribed, setIsSubscribed] = useState(false);
	const [cronExpression, setCronExpression] = useState("");

	const [postOperations] = usePostOperationsMutation();
	const [postPurchases] = usePostPurchasesMutation();
	const [postStockUsageBySite] = usePostStockusagebysiteMutation();
	const [postStockUsageBySiteByMonth] = usePostStockusagebysitebymonthMutation();
	const [postRechargeables] = usePostRechargeableitemsMutation();
	const [postBackorders] = usePostBackordersMutation();
	const [postDifot] = usePostDifotMutation();

	// Get report title safely
	const getReportTitle = () => {
		if (!reportId) return "";
		return ReportTitles[reportId as unknown as keyof typeof ReportTitles] || "";
	};

	// console.log("criteria", {
	// 	startDate,
	// 	endDate,
	// 	groupIdCollection,
	// });

	const fetchReportData = async () => {
		try {
			const criteria = {
				startDate: startDate.toUTCString(),
				endDate: endDate.toUTCString(),
				groupIdCollection: groupIdCollection,
			};

			let response;

			switch (Number(reportId)) {
				case 1:
					response = await postOperations(criteria).unwrap();
					break;
				case 2:
					response = await postPurchases(criteria).unwrap();
					break;
				case 3:
					response = await postStockUsageBySite(criteria).unwrap();
					break;
				case 4:
					response = await postStockUsageBySiteByMonth(criteria).unwrap();
					break;
				case 5:
					response = await postRechargeables(criteria).unwrap();
					break;
				case 6:
					response = await postBackorders(criteria).unwrap();
					break;
				case 7:
					response = await postDifot(criteria).unwrap();
					break;
				default:
					console.warn("Invalid report Id");
					return;
			}

			// Handle file response (Excel, PDF, etc.)
			if (response && typeof response === 'object' && '__isFileResponse' in response && response.__isFileResponse) {
				console.log("File response detected - handling download");
				const reportTitle = getReportTitle().replace(/\s+/g, '_').toLowerCase();
				const filename = `${reportTitle}_report_${new Date().toISOString().split('T')[0]}`;
				handleFileResponse(response, filename);
			} else if (response instanceof Blob || (response && typeof response === 'object' && 'data' in response && response.data instanceof Blob)) {
				// Fallback for direct blob responses
				console.log("Blob response detected - handling download");
				const reportTitle = getReportTitle().replace(/\s+/g, '_').toLowerCase();
				const filename = `${reportTitle}_report_${new Date().toISOString().split('T')[0]}`;
				handleFileResponse(response, filename);
			} else {
				// If it's still returning success message, navigate to success page
				router.navigate("reports/" + reportId + "/success");
			}
		} catch (error) {
			console.error("Report request failed", error);
		}
	};

	const handleOnDateChange = (dates: DateRange) => {
		setStartDate(dates.startDate);
		setEndDate(dates.endDate);
	};

	const handleOnSiteChange = (site: number[]) => {
		setGroupIdCollection(site);
	};

	const handleOpenSubscribeModal = () => {
		// Add subscription functionality here
		setIsSubscribed(!isSubscribed);
	};

	return (
		<ScrollContainer>
			<Container>
				<Box padding={20} gap={24}>
					<Typography.H3>Generate {getReportTitle()}</Typography.H3>
					<TitledContainer title="Select period" zIndex={32}>
						<Box padding={24}>
							<Box width="100%" maxWidth={isMobile ? "100%" : 500}>
								<Box style={{ zIndex: 50 }}>
									<EnhancedDateSelect isDateRange={true} monthOnly={isOperationsReport} onDateChange={handleOnDateChange} initialStartDate={startDate} initialEndDate={endDate} />
								</Box>

								<Box bg="$neutral100" borderRadius={8} flexDirection="row" justifyContent="space-between" gap={16} marginTop={12} padding={24} position="relative" zIndex={40}>
									<Box flex={1}>
										<Typography weight="bold">From:</Typography>
										<FormattedDate variant="DD/MM/YYYY" date={startDate} />
									</Box>

									<Box flex={1}>
										<Typography weight="bold">To:</Typography>
										<FormattedDate variant="DD/MM/YYYY" date={endDate} />
									</Box>
								</Box>
							</Box>
						</Box>
					</TitledContainer>
					<TitledContainer title="Select sites and groups" zIndex={30}>
						<Box padding={24}>
							<Box width="100%" maxWidth={isMobile ? "100%" : 500}>
								<SiteSelect borderRadius={8} isMulti={true} onSelect={(values: any[]) => handleOnSiteChange(values.map((e: any) => e.id))} title="Filter by site" />
							</Box>
						</Box>
					</TitledContainer>
					{/* <TitledContainer title="Subscribe" zIndex={30}>
						<Box padding={24}>
							<Box w={500} minWidth={"$full"} gap={16} maxWidth={"$full"}>
								<SwitchBasic text="Subscribe to this report" defaultValue={false} onChange={handleOpenSubscribeModal} />
								{isSubscribed && (
									<Box gap={16} bg="$neutral100" borderRadius={8} padding={24}>
										<Typography weight="bold">Schedule Report:</Typography>
										<ScheduleSelect onChange={(value) => setCronExpression(value || "")} />
									</Box>
								)}
							</Box>
						</Box>
					</TitledContainer> */}

					{/* <Box style={{ flexDirection: "row", gap: 16 }}> */}
					<Button borderRadius={8} variant="primary" onPress={fetchReportData}>
						<Typography sx={{ color: "$basicwhite" }}>Generate Report</Typography>
					</Button>
					{/* </Box> */}
				</Box>
			</Container>
		</ScrollContainer>
	);
}
